import { Box, Text, Tooltip } from '@wix/design-system';
import classNames from 'classnames';
import { FunctionComponent } from 'react';

import { BreadcrumbItem } from '..';
import { BreadcrumbsStructuredData } from './BreadcrumbsStructuredData';
import { TOOLTIP_ENTER_DELAY } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import useIsArticlePageRedesignUnifiedUXEnabled from '../../../hooks/useIsArticlePageRedesignUnifiedUXEnabled';
import useIsHelpCenterCopilotEnabled from '../../../hooks/useIsHelpCenterCopilotEnabled';
import { ChevronRightIcon } from '../../../icons/ChevronRightIcon';
import { InnerLink } from '../../InnerLink';

import css from './index.module.scss';

export type BreadcrumbsProps = {
  breadcrumbItem: BreadcrumbItem;
  isLastItem: boolean;
  hideChevron?: boolean;
  shallow?: boolean;
  index: number;
};

export const Breadcrumb: FunctionComponent<BreadcrumbsProps> = ({
  breadcrumbItem,
  isLastItem,
  hideChevron,
  shallow,
  index,
}: BreadcrumbsProps) => {
  const { onClick, label } = breadcrumbItem;
  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();
  const isArticlePageRedesignUnifiedUXEnabled =
    useIsArticlePageRedesignUnifiedUXEnabled();

  const BreadcrumbText: FunctionComponent<{ asLink: boolean }> = ({
    asLink,
  }) => {
    const tooltipConfig = {
      size: isHelpCenterCopilotEnabled ? 'medium' : 'small',
      maxWidth: '248px',
      placement: 'top',
      enterDelay: TOOLTIP_ENTER_DELAY,
    } as const;

    const shouldUseEllipsisTooltip =
      !isLastItem || isArticlePageRedesignUnifiedUXEnabled;

    const textElement = (
      <Text
        tabIndex={!asLink ? 0 : undefined}
        tooltipProps={shouldUseEllipsisTooltip ? tooltipConfig : { disabled: true }}
        className={classNames(css.breadcrumbItemText, {
          [css.breadcrumbItemTextLink]: asLink,
          [css.copilotBreadcrumbItemText]: isHelpCenterCopilotEnabled,
          [css.lastItemRedesign]: isLastItem,
        })}
        dataHook={`${DATA_HOOKS.BREADCRUMB}-text`}
        size={isHelpCenterCopilotEnabled ? 'medium' : 'small'}
        weight="thin"
        itemProp="name"
        ellipsis
      >
        {label}
      </Text>
    );

    return isLastItem && !isArticlePageRedesignUnifiedUXEnabled ? (
      <Tooltip
        content={label}
        size={tooltipConfig.size}
        placement={tooltipConfig.placement}
        enterDelay={tooltipConfig.enterDelay}
      >
        {textElement}
      </Tooltip>
    ) : (
      textElement
    );
  };

  return (
    <Box align="center" verticalAlign="middle" className={css.breadcrumbItem}>
      {!hideChevron && (
        <Box
          className={css.breadcrumbItemChevron}
          dataHook={DATA_HOOKS.BREADCRUMB_ITEM_SEPARATOR}
        >
          <ChevronRightIcon />
        </Box>
      )}

      <BreadcrumbsStructuredData index={index}>
        {breadcrumbItem.uri ? (
          <InnerLink
            href={breadcrumbItem.uri}
            className={css.collapseItem}
            dataHook={DATA_HOOKS.BREADCRUMB}
            onClick={onClick}
            withEnterHandler
            itemProp="item"
            shallow={shallow}
          >
            <BreadcrumbText asLink={true} />
          </InnerLink>
        ) : (
          <BreadcrumbText asLink={false} />
        )}
      </BreadcrumbsStructuredData>
    </Box>
  );
};
